import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface WarehouseByDateReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        targetDates: Date[];
    };

    items: any[];
    days: any[];
}

const state: WarehouseByDateReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        targetDates: [new Date(), new Date()],
    },

    items: [],
    days: [],
};

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).format('YYYY-MM-DD')
    }

    if (params.sortField !== null) {
        queryParams.order_by = params.sortField;
        queryParams.order_type = params.sortDirection;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<WarehouseByDateReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm =(this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action({
                            columns: {
                                'product_container_id': 'pc.id',
                                'date': 'wrd.date',
                                'product_name': 'p.name',
                                'product_container_name': 'pc.name',
                                'product_container_weight': 'pc.weight',
                                'quantity': 'wrd.quantity'
                            },
                            from: 's:warehouses_remains_daily',
                            from_as: 'wrd',
                            joins: [ 
                                {
                                    "join": "s:products",
                                    "type": "left",
                                    "as":   "p",
                                    "on": {
                                      "$eq": ["p.id", "wrd.product_id"]
                                    }
                                },
                                {
                                    "join": "s:product_containers",
                                    "type": "left",
                                    "as":   "pc",
                                    "on": {
                                      "$eq": ["pc.id", "wrd.product_container_id"]
                                    }
                                }
                            ],
                            page_size: 10000,
                            where: { 
                                "$and": [
                                    {"$eq": ["wrd.warehouse_id", {"$": params.warehouse_id}]},
                                    {"$gte": ["wrd.date", {"$": moment(params.since).format('YYYY-MM-DD')}]},
                                    {"$lte": ["wrd.date", {"$": moment(params.until).format('YYYY-MM-DD')}]},
                                ]
                            }
                        }),
                ]).then(((result: ServerResult) => {
                    commit('SET_REPORT_DATA', result.getOperation(REPORT_DATA).getContent());
                    commit('LOADED_SUCCESS');
    
                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportDataAsXLSX({rootState}, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('reports.warehouses_clients.export')
                .action({ ...buildFilter(params), format: 'xlsx'})
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')

                resolve();
            }));
        })
    },


};

export const mutations: MutationTree<WarehouseByDateReportState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.items = data.documents;
    },
};

const getters = {
    rows: (state: WarehouseByDateReportState) => {
        const days: any[] = []
        const totals: any[] = []
        const groups = state.items.reduce((acc: any, item: any) => {
            const day: any = moment(item.date).format('DD.MM.YYYY')
            if (days.indexOf(day) < 0) {
                days.push(day);
            }
            const day_index: number = days.indexOf(day)
            if (!acc[item.product_container_id]) {
                acc[item.product_container_id] = {
                    product_name: item.product_name,
                    product_container_name: item.product_container_name,
                    product_container_weight: item.product_container_weight,
                    quantities: [],
                    quantity: [],
                    display: false,
                }
            }
            acc[item.product_container_id].quantities.push(item.quantity)
            if (!totals[day_index]) {
                totals[day_index] = 0
            } 
            totals[day_index] += item.quantity * item.product_container_weight
            acc[item.product_container_id].display = acc[item.product_container_id].display || (item.quantity > 0)

            return acc
        }, {})
        return {
            days: days,
            totals: totals,
            items: Object.values(groups).filter((item: any) => {
                return item.display
            })
        }
    }
};

export const STORE_KEY = '$_warehouse_by_date_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
